import type { TrainingImageMetadataParamView, TrainingImageMetadataView } from "@/api";

// registry 이미지 metadata 를 평가 파라미터 폼 spec 으로 바꾼다. `options_from` 이 `tasks` 인
// enum 은 공개 task 이름을 선택지로 쓴다.

export type EvalParamType = "int" | "number" | "boolean" | "enum" | "string";
export type EvalParamValue = number | boolean | string;

// 선택지 value 는 컨테이너가 받는 값이다. description 은 그 선택지에 대한 이미지의 설명이고,
// 드롭다운에서 선택지 옆에 표시한다.
export interface EvalParamOption {
  value: string;
  description?: string;
  kind?: string | null;
}

export interface EvalParamSpec {
  key: string;
  label: string;
  type: EvalParamType;
  default: EvalParamValue;
  description?: string;
  min?: number;
  max?: number;
  // enum spec 만 선택지를 가진다.
  options?: readonly EvalParamOption[];
  // 이미지마다 이름이 달라도 task 기반 파라미터를 식별한다.
  selectsTask?: boolean;
}

export type EvalParamValues = Record<string, EvalParamValue>;

// metadata 타입을 폼 타입으로 바꾸고 지원하지 않는 타입은 뺀다.
const TYPE_MAP: Record<string, EvalParamType> = {
  integer: "int",
  int: "int",
  number: "number",
  float: "number",
  boolean: "boolean",
  bool: "boolean",
  enum: "enum",
  string: "string",
};

// 이미지 metadata 에는 step 이 없고, 일부 파라미터에는 범위도 없다. 그래서 값이 이산적인 경우
// 숫자 형태는 앱이 소유한다. 키는 env var 이름이고 `SEED_KEYS` 와 같은 방식으로 비교한다.
// `step` 은 값의 단위다. 정수 step 이면 metadata 선언과 무관하게 파라미터는 정수다.
// `min`/`max` 는 슬라이더의 빠진 끝만 채우고, 입력한 값은 제한하지 않는다.
type EvalParamNumericPolicy = Readonly<{ step: number; min?: number; max?: number }>;

// 이미지마다 이 파라미터 이름이 둘 중 하나이고, 결과 reader 는 둘 다 받는다. 그래서 둘에 같은
// 정책을 준다. 평가 이미지가 min/max 없이 제공하므로 범위를 여기에 둔다.
const EPISODES_POLICY: EvalParamNumericPolicy = { step: 50, min: 0, max: 1000 };

const NUMERIC_POLICIES: ReadonlyMap<string, EvalParamNumericPolicy> = new Map([
  ["NUM_ENVS", { step: 1 }],
  ["RECORD_FPS", { step: 1 }],
  ["INFERENCE_USAGE_RATIO", { step: 0.1 }],
  ["NUM_EPISODES", EPISODES_POLICY],
  ["EPISODES", EPISODES_POLICY],
]);

export function numericParamPolicy(key: string): EvalParamNumericPolicy | undefined {
  return NUMERIC_POLICIES.get(key.toUpperCase());
}

// 경계인 여기서 정한다. 그래서 입력 모드, clamp 반올림, 슬라이더 정지점이 모두 같은 답을
// 읽는다.
function resolveParamType(key: string, type: EvalParamType): EvalParamType {
  const step = numericParamPolicy(key)?.step;
  return type === "number" && step !== undefined && Number.isInteger(step) ? "int" : type;
}

// task 또는 metadata inline 목록에서 enum 선택지를 정한다. task 기반 선택지는 task 의 설명을
// 포함한다. 그래서 드롭다운이 각 시나리오를 설명할 수 있다.
function resolveOptions(
  param: TrainingImageMetadataParamView,
  tasks: TrainingImageMetadataView["tasks"],
): EvalParamOption[] | undefined {
  if (param.options_from === "tasks") {
    return tasks
      .filter((task) => task.name !== "")
      .map((task) => ({
        value: task.name,
        description: task.description ?? undefined,
        ...(task.kind === undefined ? {} : { kind: task.kind }),
      }));
  }
  return param.options?.map((option) => ({ value: option }));
}

function fallbackDefault(
  type: EvalParamType,
  options: readonly EvalParamOption[] | undefined,
): EvalParamValue {
  if (type === "boolean") return false;
  if (type === "enum") return options?.[0]?.value ?? "";
  if (type === "string") return "";
  return 0;
}

function isEvalParamValue(value: unknown): value is EvalParamValue {
  return typeof value === "number" || typeof value === "boolean" || typeof value === "string";
}

// 이미지 metadata 파라미터를 폼 spec 으로 바꾼다. 지원하지 않는 spec 과 빈 enum spec 은 뺀다.
export function metadataToParamSpecs(meta: TrainingImageMetadataView): EvalParamSpec[] {
  const tasks = meta.tasks;
  const specs: EvalParamSpec[] = [];

  for (const param of meta.params) {
    const type = param.type === null ? undefined : TYPE_MAP[param.type];
    if (!type) {
      continue;
    }

    const options = type === "enum" ? resolveOptions(param, tasks) : undefined;
    if (type === "enum" && (options == null || options.length === 0)) {
      continue;
    }

    specs.push({
      key: param.name,
      label: param.label ?? param.name,
      type: resolveParamType(param.name, type),
      default: isEvalParamValue(param.default) ? param.default : fallbackDefault(type, options),
      description: param.description ?? undefined,
      // metadata 는 범위가 없으면 null 을 쓴다. 그래서 실제 숫자만 넣는다.
      min: typeof param.min === "number" ? param.min : undefined,
      max: typeof param.max === "number" ? param.max : undefined,
      options,
      selectsTask: param.options_from === "tasks",
    });
  }

  return specs;
}

// 이미지가 공개한 시나리오 선택 파라미터를 찾는다.
export function taskParamSpec(specs: readonly EvalParamSpec[]): EvalParamSpec | null {
  return specs.find((spec) => spec.selectsTask) ?? null;
}

export function taskParamKey(specs: readonly EvalParamSpec[]): string | null {
  return taskParamSpec(specs)?.key ?? null;
}

// 시나리오를 고르지 않았으면 빈 문자열을 반환한다.
export function taskValue(specs: readonly EvalParamSpec[], values: EvalParamValues): string {
  const key = taskParamKey(specs);
  return key === null ? "" : String(values[key] ?? "");
}

// 각 spec 의 기본값으로 초기 폼 값을 만든다.
export function paramDefaults(specs: readonly EvalParamSpec[]): EvalParamValues {
  const values: EvalParamValues = {};
  for (const spec of specs) {
    values[spec.key] = spec.default;
  }
  return values;
}

// 폼 값이 이미지 기본값과 아직 같은지 알려 준다. 폼은 이 결과로 파라미터별 초기화를 표시하고,
// 확인 모달은 이 결과로 변경 수를 센다. 그래서 두 화면의 판정이 같아야 한다. 폼 입력을 거친
// 값과 비교하려고 문자열로 바꿔 비교한다(예: "8" 과 8).
export function paramValuesEqual(a: EvalParamValue, b: EvalParamValue): boolean {
  return a === b || String(a) === String(b);
}

export function formatParamValue(value: EvalParamValue): string {
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return String(value);
}

export function taskDisplayOptions(options: readonly EvalParamOption[]): EvalParamOption[] {
  const rank = (kind: string | null | undefined) =>
    kind === "benchmark" ? 0 : kind === "sandbox" ? 2 : 1;
  return [...options].sort((left, right) => rank(left.kind) - rank(right.kind));
}
